import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { resumableUploadManager } from "../service/upload.service";
import { dismissBanner, showBanner } from "../slices/bannerSlice";
import type { AppDispatch } from "../store";
import { EventType, getSystemMetadata, trackEvent } from "../utils/analytics";
import { getChunkStats } from "../utils/audioStore";
import {
  DURABILITY_GATE_SECONDS,
  DURABILITY_RECHECK_SECONDS,
  RECORDER_TIMESLICE_MS_GCS,
  USE_INDEXEDDB_BACKUP,
} from "../utils/constants";
import { useAppSelector } from "./redux-hooks";

const STALL_BANNER_ID = "recording-durability-stall";
// Must match useNoSpeechDetection's banner id; no-speech takes precedence over the stall banner.
const NO_SPEECH_BANNER_ID = "no-speech-detected";
const STALL_MESSAGE =
  "We may not be saving your recording right now — please check your connection. If this keeps showing, refresh and restart the session to be safe.";

type Capture = "live" | "dead" | "unknown";

// null = tier disabled or unverifiable; the caller treats null as UNKNOWN, never as zero.
const readLocalDurableBytes = async (audioId: number): Promise<number | null> => {
  if (!USE_INDEXEDDB_BACKUP) return null;
  const stats = await getChunkStats(audioId.toString());
  return stats?.totalBytes ?? 0;
};

/**
 * Single readiness gate, run once after DURABILITY_GATE_SECONDS of observed
 * recording: capture must be live and at least one storage tier (GCS or the
 * local store) must hold bytes. Cancels only on a verified failure; a
 * warn-only monitor re-checks for stalls afterwards. Every telemetry event
 * fires at most once per session.
 */
export function useRecordingReadiness(
  isRecording: boolean,
  isPaused: boolean,
  mediaRecorder: MediaRecorder | null,
  recordingTime: number,
  withHeadphones = false,
  onUnrecoverable: () => void = () => {},
) {
  const dispatch = useDispatch<AppDispatch>();
  const noSpeechVisible = useAppSelector((s) => s.banner.banners.some((b) => b.id === NO_SPEECH_BANNER_ID));
  const noSpeechVisibleRef = useRef(noSpeechVisible);
  noSpeechVisibleRef.current = noSpeechVisible;

  const chunksRef = useRef(0);
  const bytesRef = useRef(0);
  const gateRunRef = useRef(false);
  // recordingTime is cumulative across resumes; all timing below measures from
  // arming, or a resumed session is gated before its recorder's first chunk.
  const armedAtRef = useRef(0);
  const lastDurableBytesRef = useRef(0);
  const stallCountRef = useRef(0);
  const alertedRef = useRef(false);
  const stallEventSentRef = useRef(false);
  const sessionIdRef = useRef<number | null>(null);
  const onUnrecoverableRef = useRef(onUnrecoverable);
  onUnrecoverableRef.current = onUnrecoverable;
  const withHeadphonesRef = useRef(withHeadphones);
  withHeadphonesRef.current = withHeadphones;

  const resetSession = useCallback(() => {
    chunksRef.current = 0;
    bytesRef.current = 0;
    gateRunRef.current = false;
    armedAtRef.current = 0;
    lastDurableBytesRef.current = 0;
    stallCountRef.current = 0;
    alertedRef.current = false;
    stallEventSentRef.current = false;
  }, []);

  useEffect(() => {
    if (!isRecording || !mediaRecorder) return;
    const onData = (e: BlobEvent) => {
      if ((e.data?.size ?? 0) > 0) {
        chunksRef.current += 1;
        bytesRef.current += e.data.size;
      }
    };
    mediaRecorder.addEventListener("dataavailable", onData);
    return () => mediaRecorder.removeEventListener("dataavailable", onData);
  }, [isRecording, mediaRecorder]);

  useEffect(() => {
    if (!isRecording && recordingTime === 0) {
      resetSession();
      sessionIdRef.current = null;
      dispatch(dismissBanner(STALL_BANNER_ID));
    }
  }, [isRecording, recordingTime, dispatch, resetSession]);

  const assessCapture = useCallback((): Capture => {
    const track = mediaRecorder?.stream?.getAudioTracks?.()?.[0];
    const plumbingLive =
      (mediaRecorder?.state === "recording" || mediaRecorder?.state === "paused") &&
      mediaRecorder?.stream?.active === true &&
      track?.readyState === "live";
    const framesFlowing = chunksRef.current > 0 && bytesRef.current > 0;

    if (plumbingLive && framesFlowing) return "live";
    if (!plumbingLive) return "dead";
    // No frames is only "dead" once this session's recorder has had time to deliver a timeslice.
    const enoughElapsed = (recordingTime - armedAtRef.current) * 1000 >= 2 * RECORDER_TIMESLICE_MS_GCS;
    return enoughElapsed ? "dead" : "unknown";
  }, [mediaRecorder, recordingTime]);

  const runGate = useCallback(async () => {
    const audioId = resumableUploadManager.getAudioId();
    if (!audioId) return;

    const track = mediaRecorder?.stream?.getAudioTracks?.()?.[0];
    const capture = assessCapture();
    const committedBytes = resumableUploadManager.getCommittedBytes();
    const gcsConfirmed = resumableUploadManager.hasConfirmedGcsCommit();

    let localBytes: number | null = null;
    try {
      localBytes = await readLocalDurableBytes(audioId);
    } catch {
      localBytes = null;
    }

    const ctx = {
      audio_id: audioId,
      capture,
      gcs_confirmed: gcsConfirmed,
      committed_bytes: committedBytes,
      indexeddb_enabled: USE_INDEXEDDB_BACKUP,
      local_bytes: localBytes,
      chunks_received: chunksRef.current,
      bytes_received: bytesRef.current,
      media_recorder_state: mediaRecorder?.state,
      track_ready_state: track?.readyState,
      with_headphones: withHeadphonesRef.current,
    };

    if (capture === "dead") {
      trackEvent(EventType.RECORDING_CAPTURE_DEAD, ctx);
      Sentry.captureException(new Error(`Recording capture not live after ${DURABILITY_GATE_SECONDS}s`), {
        level: "error",
        extra: { ...ctx, systemMetadata: getSystemMetadata() },
        tags: { validation_type: "readiness_gate_capture" },
      });
      Swal.fire({
        icon: "error",
        title: "Recording Issue Detected",
        text: "We can't capture audio from your microphone. Please check your mic in the dropdown menu, or refresh the browser and try again.",
      });
      onUnrecoverableRef.current();
      return;
    }
    if (capture === "unknown") {
      trackEvent(EventType.RECORDING_DURABILITY_UNVERIFIED, ctx);
      return;
    }

    if (gcsConfirmed || (localBytes !== null && localBytes > 0)) {
      lastDurableBytesRef.current = Math.max(committedBytes, localBytes ?? 0);
      return;
    }

    const probed = await resumableUploadManager.probeGcsCommittedBytes().catch(() => null);
    if (probed !== null && probed > 0) {
      // Baseline stays local-derived; a server offset would skew the stall monitor.
      lastDurableBytesRef.current = Math.max(committedBytes, localBytes ?? 0);
      return;
    }

    // Cancel only when every applicable tier is verifiably empty; an
    // unverifiable read (probe null / local read threw) never cancels.
    const gcsVerifiedZero = probed === 0;
    const durableNo = USE_INDEXEDDB_BACKUP ? gcsVerifiedZero && localBytes === 0 : gcsVerifiedZero;

    if (!durableNo) {
      trackEvent(EventType.RECORDING_DURABILITY_UNVERIFIED, {
        ...ctx,
        probed,
        local_unverifiable: USE_INDEXEDDB_BACKUP && localBytes === null,
      });
      return;
    }

    trackEvent(EventType.RECORDING_NOT_DURABLE, { ...ctx, probed });
    Sentry.captureException(new Error(`Recording not durable after ${DURABILITY_GATE_SECONDS}s`), {
      level: "error",
      extra: { ...ctx, probed, systemMetadata: getSystemMetadata() },
      tags: { validation_type: "readiness_gate_no_tier" },
    });
    Swal.fire({
      icon: "error",
      title: "Recording Not Being Saved",
      text: "Your audio is being captured but isn't being saved anywhere. Please refresh the browser and start the session again so it records safely.",
    });
    onUnrecoverableRef.current();
  }, [mediaRecorder, assessCapture]);

  useEffect(() => {
    if (!isRecording) return;
    const audioId = resumableUploadManager.getAudioId();
    if (audioId && audioId !== sessionIdRef.current) {
      sessionIdRef.current = audioId;
      resetSession();
      armedAtRef.current = recordingTime;
    }
    if (recordingTime - armedAtRef.current >= DURABILITY_GATE_SECONDS && !gateRunRef.current) {
      gateRunRef.current = true;
      runGate();
    }
  }, [isRecording, recordingTime, runGate, resetSession]);

  // Warn-only stall monitor; never cancels, telemetry at most once per session.
  useEffect(() => {
    if (!isRecording || isPaused) return;
    const interval = setInterval(async () => {
      const audioId = resumableUploadManager.getAudioId();
      if (!audioId || bytesRef.current <= 0) return;

      let localBytes: number | null = null;
      try {
        localBytes = await readLocalDurableBytes(audioId);
      } catch {
        return;
      }
      const durableBytes = Math.max(resumableUploadManager.getCommittedBytes(), localBytes ?? 0);

      if (durableBytes > lastDurableBytesRef.current) {
        lastDurableBytesRef.current = durableBytes;
        stallCountRef.current = 0;
        if (alertedRef.current) {
          alertedRef.current = false;
          dispatch(dismissBanner(STALL_BANNER_ID));
        }
        return;
      }

      stallCountRef.current += 1;
      if (stallCountRef.current < 2 || alertedRef.current) return;

      const probed = await resumableUploadManager.probeGcsCommittedBytes().catch(() => null);
      if (probed !== null && probed > lastDurableBytesRef.current) {
        stallCountRef.current = 0;
        return;
      }
      if (probed === null && !USE_INDEXEDDB_BACKUP) return;

      alertedRef.current = true;
      if (!stallEventSentRef.current) {
        stallEventSentRef.current = true;
        trackEvent(EventType.RECORDING_DURABILITY_STALL, {
          audio_id: audioId,
          committed_bytes: resumableUploadManager.getCommittedBytes(),
          local_bytes: localBytes,
          bytes_received: bytesRef.current,
          indexeddb_enabled: USE_INDEXEDDB_BACKUP,
        });
      }
      if (!noSpeechVisibleRef.current) {
        dispatch(showBanner({ id: STALL_BANNER_ID, type: "warning", message: STALL_MESSAGE, closable: true }));
      }
    }, DURABILITY_RECHECK_SECONDS * 1000);

    return () => clearInterval(interval);
  }, [isRecording, isPaused, dispatch]);
}
